import { styled, Tab, Typography } from "@mui/material";
import { colors, tokens } from "../../../utils";

export const Container = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  "@media (max-width: 600px)": {
    gap: "16px",
    padding: "0 8px",
  },
}));

export const TabContainer = styled("div")(() => ({
  display: "flex",
  gap: "10px",
  borderRadius: "100px",
  width: "340px",
  alignSelf: "center",
  backgroundColor: "#F6F7F9",
  "@media (max-width: 600px)": {
    width: "100%",
    gap: "4px",
    padding: "0 2px",
    minWidth: 0,
    overflowX: "auto",
    whiteSpace: "nowrap",
    justifyContent: "center",
    WebkitOverflowScrolling: "touch",
  },
}));

export const StyledTab = styled(Tab)(() => ({
  textTransform: "none",
  width: "170px",
  backgroundColor: "#F6F7F9",
  padding: "10px 8px",
  // A11y (WCAG 1.4.3 AA): unselected Monthly/Annual toggle label #9A999A on
  // #F6F7F9 was 2.65:1. colors.StrokePrimary (#667085) clears 4.5:1 (4.64:1).
  // Selected pill (white on #8F38EF) unchanged.
  color: colors.StrokePrimary,
  fontSize: "14px",
  lineHeight: "20px",
  fontFamily: '"DM Sans", sans-serif',
  borderRadius: "100px",
  minWidth: "110px",
  flexShrink: 0,
  // DL-030 (ISP-4018): the selected Annual/Monthly pill is a WHITE-text purple
  // fill, so it uses the design-system ON-FILL purple (#8F38EF, white 5.28:1 AA)
  // rather than the accent #9B5AF8 (white 4.03:1, FAILS AA). on-fill != accent.
  "&.Mui-selected": {
    backgroundColor: tokens.brand.primaryActive,
    color: "#FFFFFF",
  },
  "&.Mui-focusVisible": {
    backgroundColor: tokens.brand.primaryActive,
  },
  "@media (max-width: 600px)": {
    width: "auto",
    flex: 1,
    minWidth: "110px",
    fontSize: "13px",
    padding: "12px 4px",
    flexShrink: 0,
  },
}));

export const AnnualTabContainer = styled("div")(() => ({
  display: "flex",
  gap: "6px",
  alignItems: "center",
  minWidth: 0,
  "@media (max-width: 600px)": {
    gap: "4px",
  },
}));

export const SavingsText = styled("div")(() => ({
  border: "2px solid #F5CC3B",
  backgroundColor: "#F5CC3B",
  paddingLeft: "4px",
  paddingRight: "4px",
  borderRadius: "4px",
  fontWeight: 600,
  fontFamily: '"DM Sans", sans-serif',
  fontSize: "12px",
  color: colors.primary,
  whiteSpace: "nowrap",
}));

export const PricingContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  gap: "8px",
  "@media (max-width: 600px)": {
    flexDirection: "column",
    gap: "12px",
    width: "100%",
  },
}));

// QA-129 (ISP-9999): the tier row used to just squeeze every card into one
// line (SubscriptionContainer divided 100% by the FULL tier count, so 5/6
// tiers made every card progressively narrower instead of scrolling). Wraps
// the card row so more than 4 tiers keeps the first 4 at full column width
// and scrolls horizontally to reach the rest, instead of shrinking every
// card to fit.
// T22.758: `withArrows` reserves a 52px strip above the cards for the scroll
// arrows (see ScrollArrowButton) so they never sit on top of a card.
export const PricingScrollWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "withArrows",
})(({ withArrows }) => ({
  position: "relative",
  display: "flex",
  flex: 1,
  minWidth: 0,
  alignItems: "stretch",
  paddingTop: withArrows ? "52px" : 0,
  "@media (max-width: 600px)": {
    paddingTop: 0,
    // Stack the track and the pagination dots (ordered above it, see
    // MobilePaginationDots) instead of placing the dots beside it as another
    // row item (the dots' own display:flex only decides whether THEY show a
    // row of dots, not how this wrapper lays out).
    flexDirection: "column",
  },
}));

export const TiersScrollTrack = styled("div")(({ scrollable }) => ({
  display: "flex",
  flex: 1,
  gap: "8px",
  minWidth: 0,
  overflowX: scrollable ? "auto" : "visible",
  scrollSnapType: scrollable ? "x proximity" : "none",
  scrollBehavior: "smooth",
  "@media (max-width: 600px)": {
    // T22.617 (Gordon, phone): one plan card per row. The swipe carousel
    // (85% cards + sliver/arrows/dots) still read as a second card cut off
    // at "E"/"$" with no hint, so phones stack every card full width and
    // scroll the page vertically instead of the track sideways.
    flexDirection: "column",
    gap: "12px",
    width: "100%",
    overflowX: "visible",
    scrollSnapType: "none",
  },
}));

// Round scroll-arrow affordance for the desktop 4-full-width-plus-scroll
// strategy. Only rendered by index.jsx when the track is actually
// scrollable AND there's more to reach in that direction, so it never
// appears as a dead/disabled control. On phone they sit inside the track
// edge, over the next card's peek (T22.578).
// T22.758 (T36.8 #5): the arrows used to hang over the card edges at
// top:50% / right:-16px, which is exactly where the outer card's Subscribe
// CTA sits (768: arrow x725-761 y746 over "Subscribe to ProSuite" x579-736
// y753; 1440 too). Above 600px they now sit side by side, top right, in the
// strip PricingScrollWrapper reserves above the cards: 44x44 targets that
// cover no card content.
export const ScrollArrowButton = styled("button")(({ direction }) => ({
  position: "absolute",
  top: 0,
  right: direction === "left" ? "52px" : 0,
  zIndex: 2,
  // T22.761: was 36x36 -- under the 44px target at every width.
  width: "44px",
  height: "44px",
  minWidth: "44px",
  minHeight: "44px",
  padding: 0,
  borderRadius: "50%",
  border: `1px solid ${colors.StrokePrimary}`,
  backgroundColor: "#FFFFFF",
  boxShadow: "0px 2px 8px rgba(0,0,0,0.18)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: colors.primary,
  "&:focus-visible": {
    outline: `2px solid ${tokens.brand.primaryActive}`,
    outlineOffset: "2px",
  },
  // T22.578: phone used to hide the arrows (display:none) and rely on the
  // next card's sliver + dots alone -- the reporter (S23+, 384px) read that
  // sliver as plan cards "cut off at the right". Keep the arrows on phone,
  // pulled inside the track edge (the desktop -16px offset would sit off
  // screen), so the cut card reads as "more plans" and is one tap away.
  // T22.617: phones stack the cards (see TiersScrollTrack), so the track has
  // no sideways overflow and TiersScrollRow never renders an arrow there; the
  // rule stays for any phone track that does overflow.
  "@media (max-width: 600px)": {
    top: "50%",
    transform: "translateY(-50%)",
    width: "36px",
    height: "36px",
    minWidth: "36px",
    minHeight: "36px",
    ...(direction === "left" ? { left: "4px", right: "auto" } : { right: "4px" }),
  },
}));

// QA-129 follow-up (PLQA-C-007, ISP-9999): on phone the scroll arrows are
// CSS-hidden (see ScrollArrowButton above) and the only "more plans" cue was
// a sliver of the next card peeking in at the edge -- MEASURED live on
// web-stg (390x844, 6 tiers) as an actual dead end for anyone who doesn't
// notice the peek. Dots give a persistent, always-visible affordance that
// there's more than one screen of plans, mirroring native carousel UX.
// Desktop already has click arrows, so dots stay hidden there.
// T22.617: phones stack the cards, so TiersScrollRow only renders the dots
// when the track really overflows sideways (never in the stack).
export const MobilePaginationDots = styled("div")(() => ({
  display: "none",
  "@media (max-width: 600px)": {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    // T22.211: ABOVE the cards, not below them (below sat off screen).
    order: -1,
    marginBottom: "8px",
    width: "100%",
  },
}));

export const PaginationDot = styled("span")(({ active }) => ({
  width: active ? "16px" : "6px",
  height: "6px",
  borderRadius: "3px",
  backgroundColor: active ? colors.primary : colors.StrokePrimary,
  transition: "width 0.2s ease, background-color 0.2s ease",
}));

export const LoadingContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
}));

// SUB-011: error state for a failed `subscription/packages/` fetch, replacing
// the previous silent blank. Centred in the pricing region, on-brand tokens.
export const ErrorStateContainer = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  textAlign: "center",
  padding: "48px 24px",
  minHeight: "260px",
  width: "100%",
}));

export const ErrorStateText = styled("div")(() => ({
  color: colors.StrokePrimary,
  fontFamily: '"DM Sans", sans-serif',
  fontSize: "16px",
  lineHeight: "24px",
  maxWidth: "420px",
  margin: 0,
}));

export const ErrorRetryButton = styled("button")(() => ({
  // >= 44px touch target (a11y), on-brand purple fill, keyboard-focusable.
  minHeight: "44px",
  minWidth: "140px",
  padding: "10px 24px",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  backgroundColor: tokens.brand.primaryActive,
  color: "#FFFFFF",
  fontFamily: '"DM Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 600,
  "&:focus-visible": {
    outline: `2px solid ${tokens.brand.primaryActive}`,
    outlineOffset: "2px",
  },
}));

export const PricingItem = styled("div")(
  ({ isBestDeal, isComparisonOpen, isActive }) => ({
    display: "flex",
    flex: 0.25,
    height: isComparisonOpen ? "283px" : "465px",
    flexDirection: "column",
    gap: "16px",
    padding: "16px 0px 16px 0px",
    borderRadius: "8px",
    backgroundColor: isBestDeal
      ? "#F5CC3B"
      : isActive
      ? "#9B5AF8B5"
      : "#FFFFFF",
    boxShadow: "0px 0px 5.9px 0px #00000040",
  })
);

export const BestDealBanner = styled("div")(() => ({
  background: "#F5CC3B",
  color: "#123051",
  borderRadius: "16px",
  paddingLeft: "8px",
  paddingRight: "8px",
  fontWeight: 700,
  fontSize: "14px",
  fontFamily: '"DM Sans", sans-serif',
}));

export const InformationContainer = styled("div")(({ setSpacing }) => ({
  display: "flex",
  flexDirection: "column",
  gap: setSpacing ? "8px" : "16px",
  padding: "0px 16px 0px 16px",
}));

export const Description = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  height: "110px",
}));

export const Price = styled("div")(() => ({
  display: "flex",
  gap: "8px",
  flexDirection: "column",
}));

export const AnnualPrice = styled("div")(() => ({
  display: "flex",
  gap: "4px",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px",
  backgroundColor: "#F6F7F9",
}));

export const AnnualPriceValue = styled(Typography)(() => ({
  fontSize: "14px",
  color: "#123051",
  fontWeight: 500,
  fontFamily: '"DM Sans", sans-serif',
}));

export const ButtonContainer = styled("div")(() => ({
  border: "1px solid #9B5AF8",
  borderRadius: "4px",
  padding: "8px",
  backgroundColor: "#EFE9FE",
  fontSize: "14px",
  color: "#9B5AF8",
  fontWeight: 700,
  display: "flex",
  justifyContent: "center",
}));

export const DetailsContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
}));

export const ListItem = styled("div")(() => ({
  display: "flex",
  gap: "8px",
}));

export const LastItem = styled("div")(({ isComparison = false, isActive }) => ({
  display: "flex",
  height: isComparison ? "auto" : "100%",
  alignItems: "center",
  justifyContent: "flex-end",
  flexDirection: "column",
  gap: "16px",
  backgroundColor: isActive ? "#F8F6FF" : "transparent",
}));

export const StyledLine = styled("div")(({ customColor }) => ({
  border: `1px solid ${customColor || "#E1E1E1"}`,
  width: "100%",
}));

export const HeadingContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "16px",
}));

export const CancelButtonContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  height: "70px",
}));

// ISP-9999 R3: with itemCount cards (Enterprise ON -> 5) the old
// `flex: 0 0 ${100/itemCount}%` sums the flex-bases to exactly 100% of the
// row but ignores the PricingContainer's `gap: 8px` between them -- the
// gaps then add on TOP of that 100%, pushing the row's total width past the
// viewport (measured: card right edge x=1284 vs a 1280px viewport, scrollW
// 990 vs clientW 933). `calc()` now subtracts the total gap width (gap *
// (itemCount-1)) from 100% before dividing, so N cards + (N-1) gaps sum to
// exactly the container width. `min-width: 0` lets each card shrink below
// its content's natural min-content size (the flex-item default of
// `min-width: auto` otherwise refuses to shrink a card below its widest
// child -- e.g. a feature-list line or the Enterprise CTA -- forcing the
// same overflow even once the basis math is fixed).
// QA-129 (ISP-9999): R3's fix above still divided the row by the FULL tier
// count, so 5/6 tiers kept shrinking every card instead of scrolling.
// `columns` now caps at 4 -- above 4 tiers, each card keeps a full
// 1-of-4-column width and TiersScrollTrack (see above) scrolls horizontally
// to reach the rest, matching the decided "4 full width + scroll" strategy.
// <=4 tiers is unaffected (columns === itemCount, byte-identical to before).
export const SubscriptionContainer = styled("div")(({ itemCount }) => {
  const columns = itemCount ? Math.min(itemCount, 4) : null;
  return {
    display: "flex",
    flex: columns
      ? `0 0 calc((100% - ${8 * (columns - 1)}px) / ${columns})`
      : 0.25,
    minWidth: 0,
    flexDirection: "column",
    "@media (max-width: 600px)": {
      // T22.617: full-width row in the stacked phone layout (was 85% for
      // the swipe carousel).
      flex: "0 0 auto",
      width: "100%",
    },
  };
});
